import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useConsent } from "@/context/ConsentContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Cookie, X } from "lucide-react";

const BANNER_TEXT =
  "Smart News Italia utilizza cookie tecnici necessari al funzionamento del sito e, solo con il tuo consenso, strumenti di analisi e contenuti forniti da terze parti, come YouTube. Puoi accettare tutti i cookie, rifiutare quelli non necessari oppure scegliere quali autorizzare.";

function PreferencesDialog() {
  const { prefsOpen, closePreferences, savePreferences, consent } = useConsent();
  const [analytics, setAnalytics] = useState(false);
  const [youtube, setYoutube] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (prefsOpen) {
      setAnalytics(Boolean(consent?.analytics));
      setYoutube(Boolean(consent?.youtube));
      setMarketing(Boolean(consent?.marketing));
    }
  }, [prefsOpen, consent]);

  const Row = ({ title, desc, checked, onChange, disabled, testid }) => (
    <div className="flex items-start justify-between gap-4 py-4 border-b border-border last:border-0">
      <div className="min-w-0">
        <p className="font-medium text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>
  );

  return (
    <Dialog open={prefsOpen} onOpenChange={(o) => !o && closePreferences()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="cookie-preferences-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Preferenze cookie</DialogTitle>
          <DialogDescription>
            Scegli quali categorie autorizzare. I cookie tecnici sono sempre attivi perché
            indispensabili. Nessuna categoria è preselezionata.
          </DialogDescription>
        </DialogHeader>
        <div className="py-1">
          <Row
            title="Necessari (sempre attivi)"
            desc="Cookie tecnici indispensabili per la navigazione e la sicurezza del sito. Non richiedono consenso."
            checked disabled testid="pref-necessary"
          />
          <Row
            title="Statistiche"
            desc="PostHog: statistiche sull'utilizzo del sito per migliorare il servizio. Possono essere raccolti identificatori, indirizzo IP e dati di navigazione. Nessuna registrazione delle sessioni."
            checked={analytics} onChange={setAnalytics} testid="pref-analytics"
          />
          <Row
            title="Contenuti esterni (YouTube)"
            desc="Consentono di incorporare i video di YouTube. Possono impostare cookie o altri strumenti di tracciamento di Google."
            checked={youtube} onChange={setYoutube} testid="pref-youtube"
          />
          <Row
            title="Marketing e pubblicità"
            desc="Cookie e strumenti di pubblicità e profilazione di terze parti, attivati esclusivamente con il tuo consenso."
            checked={marketing} onChange={setMarketing} testid="pref-marketing"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => savePreferences({ analytics: false, youtube: false, marketing: false })} data-testid="pref-reject-btn">
            Rifiuta i non necessari
          </Button>
          <Button className="rounded-sm" onClick={() => savePreferences({ analytics, youtube, marketing })} data-testid="pref-save-btn">
            Salva le preferenze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CookieBanner() {
  const { decided, acceptAll, rejectNonEssential, openPreferences } = useConsent();
  const { pathname } = useLocation();

  // Non mostrare il banner cookie nell'area redazione/login (non serve e non deve intercettare i click)
  if (pathname.startsWith("/admin") || pathname === "/login") return null;

  return (
    <>
      <PreferencesDialog />
      {!decided && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] p-4 sm:p-5"
          role="dialog"
          aria-label="Informativa cookie"
          data-testid="cookie-banner"
        >
          <div className="max-w-5xl mx-auto bg-card border border-border rounded-sm shadow-2xl p-5 sm:p-6 relative">
            <button
              onClick={rejectNonEssential}
              aria-label="Chiudi (mantieni solo i cookie tecnici)"
              title="Chiudi: restano attivi solo i cookie tecnici"
              className="absolute top-3 right-3 h-8 w-8 inline-flex items-center justify-center rounded-sm text-muted-foreground hover:bg-muted"
              data-testid="cookie-close-btn"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3 pr-8">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {BANNER_TEXT}{" "}
                  <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link>
                  {" · "}
                  <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <Button variant="outline" className="rounded-sm flex-1 sm:flex-none" onClick={rejectNonEssential} data-testid="cookie-reject-btn">
                    Rifiuta i non necessari
                  </Button>
                  <Button variant="outline" className="rounded-sm flex-1 sm:flex-none" onClick={openPreferences} data-testid="cookie-customize-btn">
                    Personalizza
                  </Button>
                  <Button className="rounded-sm flex-1 sm:flex-none" onClick={acceptAll} data-testid="cookie-accept-btn">
                    Accetta tutti
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
